import { sorted } from "helpers";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  ManualReviewEventProvider,
  useManualReviewEvents,
} from "components/manualReview/contexts/ManualReviewEventContext";
import { ExceptionDraftProvider } from "components/manualReview/contexts/ExceptionDraftContext";
import { MassDataProvider } from "components/manualReview/contexts/MassDataContext";
import { HotkeyDescription } from "components/identifyEventsTool/tool/HotkeyExplanation";
import { ExceptionDraftDialog } from "components/manualReview/dayExceptions/ExceptionDraftDialog";
import { TimelineExclusions } from "components/manualReview/hooks/useTimelineExclusions";
import { VerifyPeriodsFn } from "components/manualReview/hooks/useVerifySaveGuards";
import { MANUAL_REVIEW_SCOPE } from "components/manualReview/hotkeyScopes";
import { LinkedPeriodBringInProvider } from "components/manualReview/LinkedPeriodBringInDialog";
import { ManualReviewSurface } from "components/manualReview/ManualReviewSurface";
import { ManualReviewSidebarTab } from "components/manualReview/ManualReviewSidebar";
import { filterReviewPeriods } from "components/manualReview/utils/formatters";
import { labellerExclusionDecisions } from "components/manualReview/utils/timelineExclusions";
import { clampWindowToDay } from "components/manualReview/utils/timeUtils";
import { TimelineExclusionProviders } from "components/manualReview/TimelineExclusionProviders";
import { VerificationPreviewProvider } from "components/manualReview/VerificationPreviewDialog";
import {
  HotkeyProvider,
  useHotkeyContext,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import {
  useVideoControlActions,
  VideoControlProvider,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { VideoPlayerGroupProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoPlayerGroupContext";
import { useIsHotkeyScopeEnabled } from "components/scrapEyeLayout/hotkeys/useIsHotkeyScopeEnabled";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import { HEADER_HEIGHT } from "src/dashboard/components/ExpandSideMenuButton";
import {
  LoadingWindowAssignment,
  HeatSummary,
  LabellerTimeline,
  RailcarSlotMaterialSpan,
  ReviewEvent,
  ReviewPeriodData,
  ReviewSlotRevision,
} from "src/store/api/generatedApi";
import { flaggedEventRows } from "./flaggedEventRows";
import { TargetedReviewFlaggedEvents } from "./TargetedReviewFlaggedEvents";
import {
  dispositionsForGroupSave,
  getTargetedReviewHighlightColor,
  groupRowDispositions,
  targetedReviewHighlightColors,
  TargetedReviewDisposition,
  TargetedReviewPeriodGroup,
} from "./targetedReviewRows";
import { useCloseOnEscape } from "./useCloseOnEscape";
import { TARGETED_REVIEW_OMITTED_MODEL_STATUSES } from "./useTargetedReviewEvents";
import { useTargetedReviewFootageLocation } from "./useTargetedReviewFootageLocation";

dayjs.extend(utc);

const PERIOD_DATE_FORMAT = "YYYY-MM-DD";
const CONTEXT_MINUTES = 5;
const HOTKEY_OPTIONS = {
  scopes: [MANUAL_REVIEW_SCOPE],
  preventDefault: true,
  enableOnFormTags: false,
};

interface TargetedReviewPeriodInspectorProps {
  group: TargetedReviewPeriodGroup;
  location: string;
  labellerTimelines: LabellerTimeline[];
  heats: HeatSummary[];
  loadingWindows: LoadingWindowAssignment[];
  dayReviewEvents: ReviewEvent[];
  dayReviewPeriods: ReviewPeriodData[];
  reviewSlotRevisions?: ReviewSlotRevision[];
  exclusions: TimelineExclusions;
  railcarSlotMaterials: RailcarSlotMaterialSpan[];
  verifyPeriods: VerifyPeriodsFn;
  isVerifying: boolean;
  isEventsFetching: boolean;
  onDispositioned: (dispositions: TargetedReviewDisposition[]) => void;
  onClose: () => void;
}

// Replaces the table while a period is under review — the back button and
// its "Esc" hotkey (registered further down) are the only way back to the
// table, so this also carries the location/date context the table's own
// header shows.
const InspectorTopBar = ({
  location,
  group,
  onClose,
  onSkip,
  skipDisabled,
}: {
  location: string;
  group: TargetedReviewPeriodGroup;
  onClose: () => void;
  onSkip: () => void;
  skipDisabled: boolean;
}) => {
  const { t } = useTenantTranslation();
  return (
    <Box sx={{ flexShrink: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, height: HEADER_HEIGHT }}
      >
        <Tooltip title={t("close")}>
          <IconButton size="small" onClick={onClose}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>
          {location} · {group.start.format(PERIOD_DATE_FORMAT)}
        </Typography>
        <Button size="small" disabled={skipDisabled} onClick={onSkip}>
          {t("skipForNow")}
        </Button>
      </Stack>
      <Divider />
    </Box>
  );
};

interface TargetedReviewPeriodInspectorContentProps extends Omit<
  TargetedReviewPeriodInspectorProps,
  "dayReviewEvents" | "exclusions"
> {
  periodStart: Dayjs;
  periodEnd: Dayjs;
  displayStart: Dayjs;
  displayEnd: Dayjs;
  dayStart: Dayjs;
  dayEnd: Dayjs;
  windowEvents: ReviewEvent[];
  displayedPeriods: ReviewPeriodData[];
  writablePeriods: ReviewPeriodData[];
}

const TargetedReviewPeriodInspectorContent = ({
  group,
  location,
  labellerTimelines,
  heats,
  loadingWindows,
  dayReviewPeriods,
  railcarSlotMaterials,
  verifyPeriods,
  isVerifying,
  isEventsFetching,
  onDispositioned,
  onClose,
  periodStart,
  periodEnd,
  displayStart,
  displayEnd,
  dayStart,
  dayEnd,
  windowEvents,
  displayedPeriods,
  writablePeriods,
}: TargetedReviewPeriodInspectorContentProps) => {
  const { t } = useTenantTranslation();
  const {
    allEvents,
    setSelectedEventId,
    seedEventsById,
    refreshedEventIds,
    recordingEventId,
    editedSinceSeed,
  } = useManualReviewEvents();
  const { seekTo } = useVideoControlActions();
  const { getHotkey } = useHotkeyContext();
  const { isScopeEnabled } = useIsHotkeyScopeEnabled();
  const { footageLocation, canWatchOtherLocations } =
    useTargetedReviewFootageLocation({ reviewedLocation: location });

  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  const periodOffsetSeconds = periodStart.diff(displayStart, "second");
  const displayDurationSeconds = displayEnd.diff(displayStart, "second");
  useEffect(() => {
    seekTo(periodOffsetSeconds, displayDurationSeconds);
  }, [seekTo, periodOffsetSeconds, displayDurationSeconds]);

  const dispositionedRef = useRef(false);
  const disposition = useCallback(
    (dispositions: TargetedReviewDisposition[]) => {
      dispositionedRef.current = true;
      onDispositioned(dispositions);
    },
    [onDispositioned]
  );

  const verifyPeriodsAndDisposition = useCallback<VerifyPeriodsFn>(
    async (periods, eventsInScope, originalEventsInScope, options) => {
      await verifyPeriods(
        periods,
        eventsInScope,
        originalEventsInScope,
        options
      );
      disposition(
        dispositionsForGroupSave({ group, verifiedPeriods: periods })
      );
    },
    [verifyPeriods, disposition, group]
  );

  const editedRef = useRef(editedSinceSeed);
  editedRef.current = editedSinceSeed;
  const settleWithoutSave = useCallback(() => {
    if (dispositionedRef.current || editedRef.current) return;
    disposition(groupRowDispositions(group, "verified"));
  }, [disposition, group]);

  const verifyPeriodStarts = useMemo(() => {
    const starts = group.rows
      .filter((row) => row.reviewPeriod === null)
      .map((row) => row.start.toISOString());
    return starts.length > 0 ? starts : undefined;
  }, [group.rows]);

  const isRecording = recordingEventId !== null;
  const handleSkip = useCallback(() => {
    if (isVerifying || isRecording) return;
    disposition(groupRowDispositions(group, "skipped"));
  }, [isVerifying, isRecording, disposition, group]);

  useHotkeys(getHotkey("skipWindow"), handleSkip, HOTKEY_OPTIONS, [handleSkip]);
  const handlePanelKeyDown = useCloseOnEscape({
    enabled: isScopeEnabled(MANUAL_REVIEW_SCOPE),
    onClose,
  });

  const periodEvents = useMemo(
    () =>
      allEvents.filter(
        (event) =>
          !event.start.isBefore(periodStart) && event.start.isBefore(periodEnd)
      ),
    [allEvents, periodStart, periodEnd]
  );
  const flaggedRows = useMemo(
    () =>
      sorted(
        group.rows.flatMap((row) => {
          const remappedIds = row.flaggedEventIds.map(
            (id) => refreshedEventIds.get(id) ?? id
          );
          return flaggedEventRows({
            events: periodEvents.filter((event) => {
              const start = seedEventsById.get(event.id)?.start ?? event.start;
              return !start.isBefore(row.start) && start.isBefore(row.end);
            }),
            flaggedEventIds: remappedIds.filter((id) => seedEventsById.has(id)),
            visibility: remappedIds.some((id) => !seedEventsById.has(id))
              ? "all"
              : "flagged",
          });
        }),
        (row) => row.event.start.valueOf()
      ),
    [periodEvents, group.rows, seedEventsById, refreshedEventIds]
  );

  const highlightColorsByEventId = useMemo(
    () => targetedReviewHighlightColors(allEvents),
    [allEvents]
  );

  const handleSelectFlaggedEvent = useCallback(
    (eventId: string) => {
      const event = allEvents.find((candidate) => candidate.id === eventId);
      if (event) {
        seekTo(event.start.diff(displayStart, "second", true));
        setSelectedEventId(event.id, event.start);
      }
    },
    [allEvents, seekTo, displayStart, setSelectedEventId]
  );

  const sidebarExtraTabs = useMemo(
    (): ManualReviewSidebarTab[] => [
      {
        key: "flagged",
        label: t("flaggedEvents"),
        content: (
          <TargetedReviewFlaggedEvents
            rows={flaggedRows}
            onSelectEvent={handleSelectFlaggedEvent}
          />
        ),
      },
    ],
    [t, flaggedRows, handleSelectFlaggedEvent]
  );

  const extraHotkeys = useMemo(
    (): HotkeyDescription[] => [
      { hotkeyId: "skipWindow", description: t("skipForNow") },
      ...(canWatchOtherLocations
        ? [
            {
              hotkeyId: "cycleFootageLocation" as const,
              description: t("watchAnotherLocation"),
            },
          ]
        : []),
    ],
    [t, canWatchOtherLocations]
  );

  return (
    <Box
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={handlePanelKeyDown}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        outline: "none",
      }}
    >
      <InspectorTopBar
        location={location}
        group={group}
        onClose={onClose}
        onSkip={handleSkip}
        skipDisabled={isVerifying || isRecording}
      />
      <Box sx={{ flex: 1, minHeight: 0, p: 2 }}>
        <ManualReviewSurface
          location={location}
          footageLocation={footageLocation}
          reviewPeriods={writablePeriods}
          displayedReviewPeriods={displayedPeriods}
          allReviewPeriods={dayReviewPeriods}
          viewingWindowStart={periodStart}
          viewingWindowEnd={periodEnd}
          debouncedWindowStart={periodStart}
          debouncedWindowEnd={periodEnd}
          displayStart={displayStart}
          displayEnd={displayEnd}
          isEventsFetching={isEventsFetching}
          isVerifying={isVerifying}
          verifyPeriods={verifyPeriodsAndDisposition}
          goToPeriod={settleWithoutSave}
          verifyPeriodStarts={verifyPeriodStarts}
          labellerTimelines={labellerTimelines}
          railcarSlotMaterials={railcarSlotMaterials}
          reviewEvents={windowEvents}
          heats={heats}
          loadingWindows={loadingWindows}
          dayStart={dayStart}
          dayEnd={dayEnd}
          highlightColorsByEventId={highlightColorsByEventId}
          sidebarExtraTabs={sidebarExtraTabs}
          extraHotkeys={extraHotkeys}
        />
      </Box>
    </Box>
  );
};

export const TargetedReviewPeriodInspector = ({
  group,
  location,
  labellerTimelines,
  dayReviewEvents,
  dayReviewPeriods,
  reviewSlotRevisions,
  exclusions,
  ...rest
}: TargetedReviewPeriodInspectorProps) => {
  const { extra_data: extraData } = useScrapEyeMetadata();
  const hasScaleWeights = extraData.includes("mass_data");
  const tenant = useTenant();

  const periodStartMs = group.start.valueOf();
  const periodEndMs = group.end.valueOf();
  const periodStart = useMemo(() => dayjs.utc(periodStartMs), [periodStartMs]);
  const periodEnd = useMemo(() => dayjs.utc(periodEndMs), [periodEndMs]);
  const dayStart = useMemo(() => periodStart.startOf("day"), [periodStart]);
  const dayEnd = useMemo(() => dayStart.add(1, "day"), [dayStart]);
  const { start: displayStart, end: displayEnd } = useMemo(
    () =>
      clampWindowToDay(
        periodStart.subtract(CONTEXT_MINUTES, "minute"),
        periodEnd.add(CONTEXT_MINUTES, "minute"),
        dayStart,
        dayEnd
      ),
    [periodStart, periodEnd, dayStart, dayEnd]
  );
  const playableRange = useMemo(
    () => ({
      start: periodStart.diff(displayStart, "second"),
      end: periodEnd.diff(displayStart, "second"),
    }),
    [periodStart, periodEnd, displayStart]
  );

  const windowEvents = useMemo(
    () =>
      dayReviewEvents.filter((reviewEvent) => {
        const start = dayjs.utc(reviewEvent.merged.event.start);
        const end = dayjs.utc(reviewEvent.merged.event.end);
        return start.isBefore(displayEnd) && end.isAfter(displayStart);
      }),
    [dayReviewEvents, displayStart, displayEnd]
  );
  const displayedPeriods = useMemo(
    () => filterReviewPeriods(dayReviewPeriods, displayStart, displayEnd),
    [dayReviewPeriods, displayStart, displayEnd]
  );
  const writablePeriods = useMemo(
    () => filterReviewPeriods(dayReviewPeriods, periodStart, periodEnd),
    [dayReviewPeriods, periodStart, periodEnd]
  );

  const {
    excludedPeriodIds,
    materialExcludedPeriodIds,
    timingExcludedPeriodIds,
  } = exclusions;
  const labellerExclusions = useMemo(
    () =>
      labellerExclusionDecisions({
        labellerTimelines,
        excludedPeriodIds: {
          all: excludedPeriodIds,
          material: materialExcludedPeriodIds,
          timing: timingExcludedPeriodIds,
        },
      }),
    [
      labellerTimelines,
      excludedPeriodIds,
      materialExcludedPeriodIds,
      timingExcludedPeriodIds,
    ]
  );

  return (
    <HotkeyProvider>
      <VideoControlProvider autoPlay playableRange={playableRange}>
        <VideoPlayerGroupProvider location={location}>
          <ExceptionDraftProvider location={location}>
            <VerificationPreviewProvider>
              <ManualReviewEventProvider
                events={windowEvents}
                reviewSlotRevisions={reviewSlotRevisions}
                location={location}
                videoStart={displayStart}
                windowStart={displayStart}
                windowEnd={displayEnd}
                writableStart={periodStart}
                writableEnd={periodEnd}
                areaMaterialStorageKey={`targeted-review-${tenant}-${location}`}
              >
                <TimelineExclusionProviders exclusions={exclusions}>
                  <MassDataProvider
                    windowStart={displayStart}
                    windowEnd={displayEnd}
                    location={location}
                    enabled={hasScaleWeights}
                  >
                    <LinkedPeriodBringInProvider
                      location={location}
                      labellerExclusions={labellerExclusions}
                      omittedModelStatuses={
                        TARGETED_REVIEW_OMITTED_MODEL_STATUSES
                      }
                      getHighlightColor={getTargetedReviewHighlightColor}
                    >
                      <TargetedReviewPeriodInspectorContent
                        {...rest}
                        group={group}
                        location={location}
                        labellerTimelines={labellerTimelines}
                        dayReviewPeriods={dayReviewPeriods}
                        periodStart={periodStart}
                        periodEnd={periodEnd}
                        displayStart={displayStart}
                        displayEnd={displayEnd}
                        dayStart={dayStart}
                        dayEnd={dayEnd}
                        windowEvents={windowEvents}
                        displayedPeriods={displayedPeriods}
                        writablePeriods={writablePeriods}
                      />
                    </LinkedPeriodBringInProvider>
                  </MassDataProvider>
                </TimelineExclusionProviders>
              </ManualReviewEventProvider>
            </VerificationPreviewProvider>
            <ExceptionDraftDialog />
          </ExceptionDraftProvider>
        </VideoPlayerGroupProvider>
      </VideoControlProvider>
    </HotkeyProvider>
  );
};
